// Aktenuebernahme MVP AP5 (20.10.2026): Hintergrund-Uebernahme + fachliche Pruefung in der bestehenden Aktenuebernahme-Seite.
// Alles Wesentliche liegt serverseitig (Fortschritt, Status, Entscheidungen) -> nach Seitenwechsel/Neustart unveraendert sichtbar.
// Ohne aktivierten Server-Schalter (404) blendet sich das Panel aus. Keine automatische Fachfreigabe: jede Aenderung ist ein Klick
// mit ausdruecklicher Entscheidung; fremde Klienten werden in der Trennung nie vorbelegt.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  UI_STATUS_LABEL, UI_STATUS_FARBE, TYP_REITER, SPERRGRUND_TEXT, TRENN_SPERRGRUND_TEXT,
  bestandHinweis, zielVorbelegung, trennungPruefen, sammelErlaubt, nachTyp, offenZahl, bestaetigbar, gateLabel, vorwahlJob,
} from '@/lib/aktenuebernahmeJob';

const merke = (k, v) => { try { localStorage.setItem(k, v); } catch { /* nur Komfort */ } };
const lies = (k) => { try { return localStorage.getItem(k); } catch { return null; } };

function Badge({ status }) {
  return <span className={`px-2 py-0.5 rounded text-xs ${UI_STATUS_FARBE[status] || 'bg-slate-700'}`}>{UI_STATUS_LABEL[status] || status}</span>;
}

export default function UebernahmePruefPanel({ klient, klienten, api, headers: rohHeaders }) {
  const auth = rohHeaders && rohHeaders.Authorization;
  const headers = useMemo(() => ({ Authorization: auth }), [auth]);       // stabil -> kein Lade-Kreislauf
  const [originalUrl, setOriginalUrl] = useState(null);
  const [verfuegbar, setVerfuegbar] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(null);
  const [dateien, setDateien] = useState([]);
  const [vorschlaege, setVorschlaege] = useState([]);
  const [reiter, setReiter] = useState(lies('au_reiter') || 'aufgabe');
  const [auswahl, setAuswahl] = useState([]);
  const [trennung, setTrennung] = useState(null);
  const [einheiten, setEinheiten] = useState([]);
  const [laedt, setLaedt] = useState(false);
  const base = `${api}/api/aktenuebernahme-pruefung`;
  const jh = useMemo(() => ({ ...headers, 'Content-Type': 'application/json' }), [headers]);

  const laden = useCallback(async () => {
    if (!klient) return;
    const r = await fetch(`${base}/klient/${klient.id}/jobs`, { headers });
    if (r.status === 404) { setVerfuegbar(false); return; }
    if (!r.ok) return;
    const js = await r.json();
    setJobs(js);
    const gemerkt = lies(`au_job_${klient.id}`);
    const id = vorwahlJob(js, jobId || gemerkt);                 // AP6a: Ursprungsjob statt Teil-Job vorwaehlen
    setJobId(id);
    if (id) {
      const d = await fetch(`${base}/job/${id}/dateien`, { headers });
      if (d.ok) setDateien(await d.json());
    } else setDateien([]);
    const v = await fetch(`${base}/klient/${klient.id}/vorschlaege`, { headers });
    if (v.ok) setVorschlaege(await v.json());
  }, [klient, jobId, base, headers]);

  useEffect(() => { laden(); }, [laden]);
  useEffect(() => {                                   // Fortschritt laufender Jobs nachladen (Server arbeitet unabhaengig weiter)
    const laeuft = jobs.some((j) => ['angelegt', 'laeuft'].includes(j.status));
    if (!laeuft) return undefined;
    const t = setInterval(laden, 5000);
    return () => clearInterval(t);
  }, [jobs, laden]);

  if (!klient || !verfuegbar) return null;
  const job = jobs.find((j) => j.id === jobId);
  const gruppen = nachTyp(vorschlaege);
  const sichtbar = gruppen[reiter] || [];
  const ausgewaehlt = sichtbar.filter((v) => auswahl.includes(v.id));
  const klientName = (id) => {
    if (id === 'unzugeordnet') return 'unzugeordnet (manuelle Klärung)';
    const k = (klienten || []).find((x) => x.id === id);
    return k ? `${k.nachname || ''}, ${k.vorname || ''}` : (id ? `${String(id).slice(0, 8)}…` : '–');
  };

  const post = async (url, body, okText) => {
    setLaedt(true);
    try {
      const r = await fetch(url, { method: 'POST', headers: jh, body: JSON.stringify(body || {}) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(`Nicht ausgeführt: ${j.detail || r.status}`); return null; }   // nie als Erfolg melden
      if (j.status === 'wird_angewendet') toast.info('Freigabe läuft noch – noch nicht übernommen, bitte später fortsetzen');   // nie als Erfolg melden
      else if (okText) toast.success(okText);
      return j;
    } finally { setLaedt(false); await laden(); }
  };

  const starten = async () => {
    const r = await fetch(`${api}/api/aktenuebernahme-job/start`, { method: 'POST', headers: jh, body: JSON.stringify({ klient_id: klient.id }) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(`Start nicht möglich: ${j.detail || r.status}`); return; }
    merke(`au_job_${klient.id}`, j.id); setJobId(j.id); toast.success('Hintergrund-Übernahme gestartet – läuft serverseitig weiter'); laden();
  };

  const trennungOeffnen = async (vid) => {
    const r = await fetch(`${base}/trennung/${vid}`, { headers });
    if (!r.ok) { toast.error('Trennvorschlag nicht ladbar'); return; }
    const v = await r.json();
    setTrennung(v);
    // Original authentifiziert laden (ein iframe kann keinen Bearer-Header senden)
    const o = await fetch(`${base}/trennung/${vid}/original`, { headers });
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    setOriginalUrl(o.ok ? URL.createObjectURL(await o.blob()) : null);
    setEinheiten((v.einheiten_vorschlag || []).map((e) => ({ von: e.von, bis: e.bis, klient_id: zielVorbelegung(e, v.klient_id) })));
  };
  const trennFehler = trennung ? trennungPruefen(einheiten, trennung.seitenzahl) : null;

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="uebernahme-pruef-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-slate-200">Hintergrund-Übernahme und fachliche Prüfung</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm text-slate-300">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={starten} data-testid="au-start">Übernahme im Hintergrund starten</Button>
          <select className="bg-slate-800 rounded px-2 py-1" value={jobId || ''} data-testid="au-job-wahl"
                  onChange={(e) => { setJobId(e.target.value); merke(`au_job_${klient.id}`, e.target.value); }}>
            {jobs.map((j) => <option key={j.id} value={j.id}>{new Date(j.erstellt_am).toLocaleString('de-DE')} – {j.status}{j.nur_explizite_dateien ? ' (Teil aus Trennung)' : ''}</option>)}
          </select>
          <span className="text-xs text-slate-500">Fortschritt und Entscheidungen werden serverseitig gespeichert.</span>
        </div>

        {job && (
          <div data-testid="au-fortschritt">
            <div className="h-2 bg-slate-800 rounded"><div className="h-2 bg-sky-600 rounded" style={{ width: `${job.fortschritt.prozent_technisch}%` }} /></div>
            <div className="mt-1 flex flex-wrap gap-3 text-xs">
              {Object.entries(job.fortschritt.je_status).map(([s, n]) => <span key={s}><Badge status={s} /> {n}</span>)}
              <span className="text-slate-500">fachlich abgeschlossen: {job.fortschritt.abgeschlossen}/{job.fortschritt.gesamt}</span>
            </div>
          </div>
        )}

        <table className="w-full text-xs" data-testid="au-dateien">
          <thead><tr className="text-slate-500 text-left"><th>Dokument</th><th>Status</th><th>Hinweise</th><th>Offen</th><th /></tr></thead>
          <tbody>
            {dateien.map((d) => (
              <tr key={d.id} className="border-t border-slate-800">
                <td className="py-1">{d.rel_pfad}</td>
                <td><Badge status={d.ui_status} /></td>
                <td className="text-slate-400">
                  {d.sperrgrund && <span>{TRENN_SPERRGRUND_TEXT[d.sperrgrund] || d.sperrgrund}. </span>}
                  {d.analyse && d.analyse.vollstaendig === false && <span>Analyse unvollständig. </span>}
                  {d.zurueckgestellt && <span>KI vorübergehend nicht erreichbar – wird erneut versucht. </span>}
                  {d.dublette_von && <span>Dublette eines vorhandenen Dokuments. </span>}
                  {d.hash_bei_anderem_klient && <span>Gleiche Datei liegt bei einem anderen Klienten. </span>}
                  {d.fehler && <span>{d.fehler}</span>}
                </td>
                <td>{d.entscheidungen_offen || 0}</td>
                <td className="text-right space-x-1">
                  {d.trennvorschlag_id && d.status === 'trennung_offen' && <Button size="sm" variant="outline" onClick={() => trennungOeffnen(d.trennvorschlag_id)}>Trennung prüfen</Button>}
                  {bestaetigbar(d) && <Button size="sm" variant="outline" disabled={laedt}
                    onClick={() => post(`${base}/datei/${d.id}/bestaetigen`, {}, 'Dokument als fachlich geprüft bestätigt')}>Als geprüft bestätigen</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {trennung && (
          <div className="border border-red-900 rounded p-3 space-y-2" data-testid="au-trennung">
            <div className="font-medium text-slate-200">Trennentscheidung: {trennung.rel_pfad} ({trennung.seitenzahl} Seiten) – {TRENN_SPERRGRUND_TEXT[trennung.sperrgrund] || trennung.sperrgrund}</div>
            <div className="grid md:grid-cols-2 gap-3">
              {originalUrl ? <iframe title="Original" className="w-full h-96 bg-white rounded" src={originalUrl} />
                : <div className="text-xs text-amber-300">Original nicht verfügbar.</div>}
              <div>
                <table className="w-full text-xs mb-2">
                  <thead><tr className="text-slate-500 text-left"><th>Seite</th><th>Erkannt</th><th>Merkmale</th></tr></thead>
                  <tbody>{(trennung.seiten || []).map((s) => (
                    <tr key={s.seite} className="border-t border-slate-800"><td>{s.seite}</td><td>{s.leer ? 'leer' : klientName(s.klient_id)}</td>
                      <td className="text-slate-500">{[s.seite_x_von_y && `S. ${s.seite_x_von_y[0]}/${s.seite_x_von_y[1]}`, s.briefanfang && 'Briefanfang', s.anlage && 'Anlage',
                        ...(s.aktenzeichen || []), ...(s.bg_nummern || [])].filter(Boolean).join(' · ')}</td></tr>))}</tbody>
                </table>
                {einheiten.map((e, i) => (
                  <div key={i} className="flex items-center gap-1 mb-1">
                    S.<input className="w-12 bg-slate-800 rounded px-1" value={e.von} onChange={(x) => setEinheiten(einheiten.map((y, j) => (j === i ? { ...y, von: x.target.value } : y)))} />
                    –<input className="w-12 bg-slate-800 rounded px-1" value={e.bis} onChange={(x) => setEinheiten(einheiten.map((y, j) => (j === i ? { ...y, bis: x.target.value } : y)))} />
                    <select className="bg-slate-800 rounded px-1 flex-1" value={e.klient_id} onChange={(x) => setEinheiten(einheiten.map((y, j) => (j === i ? { ...y, klient_id: x.target.value } : y)))}>
                      <option value="">– Ziel wählen –</option>
                      <option value="unzugeordnet">unzugeordnet (manuelle Klärung)</option>
                      {(klienten || []).map((k) => <option key={k.id} value={k.id}>{k.nachname}, {k.vorname}</option>)}
                    </select>
                    <Button size="sm" variant="ghost" onClick={() => setEinheiten(einheiten.filter((_, j) => j !== i))}>✕</Button>
                  </div>
                ))}
                <Button size="sm" variant="ghost" onClick={() => setEinheiten([...einheiten, { von: '', bis: '', klient_id: '' }])}>+ Bereich</Button>
                {trennFehler && <div className="text-amber-300 text-xs">{trennFehler}</div>}
                <div className="flex gap-2 mt-2">
                  <Button size="sm" disabled={!!trennFehler || laedt} data-testid="au-trennen"
                    onClick={async () => { const r = await post(`${base}/trennung/${trennung.id}/entscheidung`, { einheiten: einheiten.map((e) => ({ von: Number(e.von), bis: Number(e.bis), klient_id: e.klient_id })) }, 'Trennung ausgeführt – Teile werden einzeln analysiert'); if (r) setTrennung(null); }}>
                    Trennung bestätigen</Button>
                  <Button size="sm" variant="outline" disabled={laedt}
                    onClick={async () => { const r = await post(`${base}/trennung/${trennung.id}/entscheidung`, { ablehnen: true }, 'Keine Trennung – Dokument bleibt gesperrt'); if (r) setTrennung(null); }}>Nicht trennen</Button>
                  <Button size="sm" variant="ghost" onClick={() => setTrennung(null)}>Schließen</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div data-testid="au-vorschlaege">
          <div className="flex flex-wrap gap-1 mb-2">
            {TYP_REITER.map((r) => (
              <Button key={r.typ} size="sm" variant={reiter === r.typ ? 'default' : 'ghost'} onClick={() => { setReiter(r.typ); merke('au_reiter', r.typ); setAuswahl([]); }}>
                {r.label} ({offenZahl(gruppen[r.typ])}/{(gruppen[r.typ] || []).length})
              </Button>))}
          </div>
          {sichtbar.length === 0 && <div className="text-slate-500 text-xs">Keine Vorschläge in diesem Bereich.</div>}
          {sichtbar.map((v) => (
            <div key={v.id} className="border-t border-slate-800 py-2 flex gap-2" data-testid="au-vorschlag">
              <input type="checkbox" disabled={v.status !== 'offen'} checked={auswahl.includes(v.id)}
                     onChange={(e) => setAuswahl(e.target.checked ? [...auswahl, v.id] : auswahl.filter((x) => x !== v.id))} />
              <div className="flex-1">
                <div className="text-slate-200">
                  {v.inhalt.titel || v.inhalt.bezeichnung || (v.typ === 'stammdaten' ? `${v.inhalt.feld}: ${v.inhalt.wert}` : '') || v.inhalt.iban || v.inhalt.glaeubiger || v.inhalt.vorgang_typ}
                  {v.inhalt.faellig_am && <span className="text-slate-400"> · fällig {v.inhalt.faellig_am}</span>}
                </div>
                <div className="text-xs text-slate-400">
                  Status: {v.status}{v.sperrgrund ? ` – ${SPERRGRUND_TEXT[v.sperrgrund] || v.sperrgrund}` : ''}
                  {bestandHinweis(v) && <span> · {bestandHinweis(v)}</span>}
                  {' · '}Belege: {(v.belege || []).map((b) => `${String(b.dokument_id).slice(0, 8)}${b.seite ? ` S.${b.seite}` : ''}`).join(', ')}
                  {v.gate && <span data-testid="au-gate"> · Prüf-Gate: {gateLabel(v.gate)}</span>}
                </div>
                <div className="text-[11px] text-slate-500">Vorschlag aus dem Originaldokument – keine automatische Übernahme.</div>
              </div>
              <div className="flex flex-col gap-1">
                {v.status === 'offen' && <Button size="sm" disabled={laedt} onClick={() => post(`${base}/vorschlag/${v.id}/entscheidung`, { entscheidung: 'freigeben' }, 'Freigegeben')}>Freigeben</Button>}
                {v.status === 'gesperrt' && v.sperrgrund === 'konflikt' && <>
                  <Button size="sm" disabled={laedt} onClick={() => post(`${base}/vorschlag/${v.id}/entscheidung`, { entscheidung: 'freigeben', klaerung: 'neu' }, 'Fundwert übernommen')}>Fundwert übernehmen</Button>
                  <Button size="sm" variant="outline" disabled={laedt} onClick={() => post(`${base}/vorschlag/${v.id}/entscheidung`, { entscheidung: 'freigeben', klaerung: 'bestand' }, 'Bestand behalten')}>Bestand behalten</Button>
                </>}
                {v.status === 'wird_angewendet' && <Button size="sm" disabled={laedt} data-testid="au-fortsetzen"
                  onClick={() => post(`${base}/vorschlag/${v.id}/entscheidung`, { entscheidung: 'freigeben' }, 'Unterbrochene Entscheidung abgeschlossen')}>Unterbrochene Entscheidung fortsetzen</Button>}
                {['offen', 'gesperrt', 'abgedeckt'].includes(v.status) && <Button size="sm" variant="ghost" disabled={laedt}
                  onClick={() => post(`${base}/vorschlag/${v.id}/entscheidung`, { entscheidung: 'ablehnen' }, 'Abgelehnt')}>Ablehnen</Button>}
              </div>
            </div>
          ))}
          {ausgewaehlt.length > 1 && (
            <div className="flex items-center gap-2 mt-2" data-testid="au-sammel">
              <Button size="sm" disabled={!sammelErlaubt(ausgewaehlt) || laedt}
                onClick={async () => { const r = await post(`${base}/vorschlaege/sammel`, { ids: ausgewaehlt.map((v) => v.id), entscheidung: 'freigeben' }, `${ausgewaehlt.length} Vorschläge einzeln protokolliert freigegeben`); if (r) setAuswahl([]); }}>
                {ausgewaehlt.length} gemeinsam freigeben</Button>
              {!sammelErlaubt(ausgewaehlt) && <span className="text-xs text-amber-300">Nur gleichartige, offene Vorschläge derselben Vorgangsart gemeinsam.</span>}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
